import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { ApiError } from '@/util/errors/api-error'
import { TimelineEventsActions } from '@/timeline/events/store/slice'
import { TimelineReferencesActions } from '@/timeline/references/store/slice'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { kTimelineAssetPollIntervalsMs } from '../constants'
import { getTimelineAssetByMatter } from '../fetch/get-timeline-asset-by-matter'
import { getTimelineAsset } from '../fetch/get-timeline-asset'
import { getTimelineAssetEvents } from '../fetch/get-timeline-asset-events'
import { getTimelineAssetFiles } from '../fetch/get-timeline-asset-files'
import { getTimelineAssetUpdates } from '../fetch/get-timeline-asset-updates'
import { getUpdateStatus } from '../fetch/get-update-status'
import { postInitializeTimeline } from '../fetch/post-initialize-timeline'
import { postApplyUpdate } from '../fetch/post-apply-update'
import { postRetryFailed } from '../fetch/post-retry-failed'
import { postCompleteExcludingFailed } from '../fetch/post-complete-excluding-failed'
import { kActiveTimelineUpdateStatuses, TimelineUpdateStatus } from './schemas'
import { TimelineAssetActions } from './slice'

export const timelineAssetListenerMiddleware = createListenerMiddleware()
const startListener = timelineAssetListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

const kConflictMessage = 'The timeline is being updated. Try again when the current update finishes.'
const kGenericMutationMessage = 'Something went wrong. Please try again.'

// Give up polling after this many consecutive status-fetch failures (transient errors retry).
const kTimelineAssetMaxPollFailures = 5

// Assets with an in-flight poll loop. De-duping per asset (instead of a global
// listener unsubscribe) lets different matters/assets poll concurrently — a global
// take-leading guard dropped a second matter's poll when you switched to it and never
// resumed it. Exported so tests can reset it between cases (module-level state).
export const pollingAssetIds = new Set<string>()

/**
 * Listener: Fetch Asset For Matter
 * Loads the matter's living timeline overview (or its confirmed absence), then resumes
 * polling of any in-flight update run and refreshes events/files when stale.
 */
startListener({
  actionCreator: TimelineAssetActions.fetchAssetForMatter,
  effect: async (action, listenerApi) => {
    const { matterId } = action.payload
    const state = listenerApi.getState().timelineAssetState

    if (state.assetLoadingByMatterId[matterId]) return

    try {
      listenerApi.dispatch(TimelineAssetActions.setAssetLoading({ matterId, isLoading: true }))
      listenerApi.dispatch(TimelineAssetActions.setAssetError({ matterId, hasError: false }))

      const response = await getTimelineAssetByMatter(matterId)
      listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId, asset: response.asset }))

      const asset = response.asset
      if (!asset) return

      // Resume polling any in-flight run (FAILED_FILES gets a one-shot status fetch inside)
      if (asset.active_update) {
        listenerApi.dispatch(TimelineAssetActions.pollActiveUpdate({ assetId: asset.id, updateId: asset.active_update.id }))
      }

      // Refresh events/files when the live timeline version is not the one loaded
      const loadedVersion = listenerApi.getState().timelineAssetState.loadedEventsVersionByAssetId[asset.id]
      if (loadedVersion !== asset.chronology_version) {
        listenerApi.dispatch(TimelineAssetActions.refreshAssetData({ assetId: asset.id }))
      }
    } catch {
      listenerApi.dispatch(TimelineAssetActions.setAssetError({ matterId, hasError: true }))
    } finally {
      listenerApi.dispatch(TimelineAssetActions.setAssetLoading({ matterId, isLoading: false }))
    }
  },
})

/**
 * Listener: Refresh Asset Data
 * Fetches the ordered live events + source-file roster. Events/references go into the
 * shared timeline slices keyed by assetId (the table/popovers read by store key).
 */
startListener({
  actionCreator: TimelineAssetActions.refreshAssetData,
  effect: async (action, listenerApi) => {
    const { assetId } = action.payload

    try {
      listenerApi.dispatch(TimelineAssetActions.setDataError({ assetId, hasError: false }))

      const [eventsResponse, filesResponse] = await Promise.all([getTimelineAssetEvents(assetId), getTimelineAssetFiles(assetId)])

      listenerApi.dispatch(TimelineEventsActions.setJobEventsList({ timelineJobId: assetId, events: eventsResponse.events }))
      listenerApi.dispatch(TimelineReferencesActions.setJobReferencesRecord({ timelineJobId: assetId, references: eventsResponse.references }))
      listenerApi.dispatch(TimelineAssetActions.setLoadedEventsVersion({ assetId, version: eventsResponse.chronology_version }))
      listenerApi.dispatch(TimelineAssetActions.setFilesForAsset({ assetId, files: filesResponse.files }))
    } catch {
      listenerApi.dispatch(TimelineAssetActions.setDataError({ assetId, hasError: true }))
    }
  },
})

/**
 * Listener: Fetch Update History
 * Loads the asset's full update history (newest-first) into the store for the version panel.
 */
startListener({
  actionCreator: TimelineAssetActions.fetchUpdateHistory,
  effect: async (action, listenerApi) => {
    const { assetId } = action.payload

    try {
      const response = await getTimelineAssetUpdates(assetId)
      listenerApi.dispatch(TimelineAssetActions.setUpdateHistory({ assetId, updates: response.updates }))
    } catch (e) {
      console.error('Error fetching timeline update history:', e)
    }
  },
})

/**
 * Listener: Poll Active Update
 * Polls one update run with 5s -> 10s -> 15s backoff until it leaves the active statuses.
 * De-dupes per asset so re-dispatching for an asset already polling is a no-op, while
 * different assets still poll concurrently (so switching matters doesn't drop a poll).
 * Exits: DONE -> refresh overview + data; FAILED_FILES -> keep status + refresh roster;
 * ERROR -> keep status (update.id doubles as the user-facing error ID).
 */
startListener({
  actionCreator: TimelineAssetActions.pollActiveUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    if (pollingAssetIds.has(assetId)) return
    pollingAssetIds.add(assetId)
    try {
      let pollCount = 0
      let consecutiveErrors = 0

      for (;;) {
        // A transient status-fetch error must not kill the loop; swallow it and retry on
        // the next backoff tick so the banner keeps polling instead of spinning forever.
        let status
        try {
          status = await getUpdateStatus(assetId, updateId)
          consecutiveErrors = 0
        } catch (e) {
          consecutiveErrors++
          console.error(`Error polling timeline update (failure ${consecutiveErrors}/${kTimelineAssetMaxPollFailures}):`, e)
          if (consecutiveErrors >= kTimelineAssetMaxPollFailures) return
          const delay = kTimelineAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kTimelineAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        listenerApi.dispatch(TimelineAssetActions.setUpdateStatus({ assetId, status }))

        if (kActiveTimelineUpdateStatuses.includes(status.update.status)) {
          const delay = kTimelineAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kTimelineAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        if (status.update.status === TimelineUpdateStatus.DONE) {
          const overview = await getTimelineAsset(assetId)
          listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
          listenerApi.dispatch(TimelineAssetActions.refreshAssetData({ assetId }))
          listenerApi.dispatch(TimelineAssetActions.fetchUpdateHistory({ assetId }))
        } else if (status.update.status === TimelineUpdateStatus.FAILED_FILES) {
          const files = await getTimelineAssetFiles(assetId)
          listenerApi.dispatch(TimelineAssetActions.setFilesForAsset({ assetId, files: files.files }))
        }
        return
      }
    } catch (e) {
      console.error('Error polling timeline update:', e)
    } finally {
      // Release the per-asset guard so a later run on this asset (or coming back to the matter)
      // can start a fresh poll loop. Without this the asset stays "polling" forever and re-dispatches no-op.
      pollingAssetIds.delete(assetId)
    }
  },
})

/**
 * Listener: Initialize Timeline
 * Creates the living timeline from the staged file selection and starts polling its
 * INITIALIZE run. A 409 means the matter already has one - refetch and surface inline.
 */
startListener({
  actionCreator: TimelineAssetActions.initializeTimeline,
  effect: async (action, listenerApi) => {
    const { matterId, userFileIds } = action.payload

    try {
      listenerApi.dispatch(TimelineAssetActions.setInitializePending({ matterId, isPending: true }))
      listenerApi.dispatch(TimelineAssetActions.setInitializeError({ matterId, error: null }))

      const response = await postInitializeTimeline({ matter_id: matterId, user_file_ids: userFileIds })
      listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId, asset: response.asset }))
      listenerApi.dispatch(TimelineAssetActions.pollActiveUpdate({ assetId: response.asset.id, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(TimelineAssetActions.resetAddFilesStaging({ matterId }))
    } catch (e) {
      const message = e instanceof ApiError && e.status === 409 ? 'This matter already has a timeline.' : kGenericMutationMessage
      listenerApi.dispatch(TimelineAssetActions.setInitializeError({ matterId, error: message }))
      try {
        listenerApi.dispatch(TimelineAssetActions.fetchAssetForMatter({ matterId }))
      } catch (refetchError) {
        console.error('Error refetching timeline after initialize failure:', refetchError)
      }
    } finally {
      listenerApi.dispatch(TimelineAssetActions.setInitializePending({ matterId, isPending: false }))
    }
  },
})

/**
 * Listener: Apply Update (Add Files)
 * Enqueues an ADD_FILES run and starts polling it. A 409 means another run is active -
 * surface inline and refetch the overview so the UI shows that run.
 */
startListener({
  actionCreator: TimelineAssetActions.applyUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, addUserFileIds } = action.payload

    try {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: null }))

      const response = await postApplyUpdate(assetId, { add_user_file_ids: addUserFileIds })

      const overview = await getTimelineAsset(assetId)
      listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      // The roster rows for the added files are created synchronously when the run is enqueued, so
      // refresh the Source Files roster now — they show as Pending/Processing instead of only after DONE.
      const files = await getTimelineAssetFiles(assetId)
      listenerApi.dispatch(TimelineAssetActions.setFilesForAsset({ assetId, files: files.files }))
      listenerApi.dispatch(TimelineAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(TimelineAssetActions.resetAddFilesStaging({ matterId: overview.matter_id }))
    } catch (e) {
      const message = e instanceof ApiError && e.status === 409 ? kConflictMessage : kGenericMutationMessage
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: message }))
      try {
        const overview = await getTimelineAsset(assetId)
        listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
        const files = await getTimelineAssetFiles(assetId)
        listenerApi.dispatch(TimelineAssetActions.setFilesForAsset({ assetId, files: files.files }))
        if (overview.active_update) {
          listenerApi.dispatch(TimelineAssetActions.pollActiveUpdate({ assetId, updateId: overview.active_update.id }))
        }
      } catch (refetchError) {
        console.error('Error refetching timeline after update failure:', refetchError)
      }
    } finally {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Retry Failed Files
 * Re-enqueues a FAILED_FILES run for its failed files only, then resumes polling.
 */
startListener({
  actionCreator: TimelineAssetActions.retryFailedFiles,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: null }))

      const response = await postRetryFailed(assetId, updateId)
      // Refresh the overview so active_update points to the retry run. Otherwise the stale
      // FAILED_FILES active_update keeps selectFailedFilesUpdate returning the old decision, so the
      // "Retry / Continue without failed files" banner stays up while the retry is already running.
      const overview = await getTimelineAsset(assetId)
      listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(TimelineAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
    } catch {
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Complete Excluding Failed
 * Publishes the staged successes of a FAILED_FILES run and excludes its failures,
 * then refreshes the overview and timeline data.
 */
startListener({
  actionCreator: TimelineAssetActions.completeExcludingFailed,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: null }))

      const status = await postCompleteExcludingFailed(assetId, updateId)
      listenerApi.dispatch(TimelineAssetActions.setUpdateStatus({ assetId, status }))

      const overview = await getTimelineAsset(assetId)
      listenerApi.dispatch(TimelineAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(TimelineAssetActions.refreshAssetData({ assetId }))
    } catch {
      listenerApi.dispatch(TimelineAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(TimelineAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Stage Initialized Upload
 * Converts a pending Add Files upload into a staged file the moment its user_file id exists.
 * Keyed on the shared uppy action because BOTH upload drivers dispatch it — the Add Files
 * page pipeline and the MatterLayout fallback that adopts uploads stranded when the page
 * unmounts mid-batch. Living here (not in the page hook) is what guarantees a fallback-driven
 * upload still lands in the staged selection instead of deadlocking the CTA as a forever-pending
 * card. Uploads not registered by the timeline Add Files page are never in the attached list.
 */
startListener({
  actionCreator: UppyFileUploadActions.fileInitialized,
  effect: (action, listenerApi) => {
    const { clientTmpId, serverFileId } = action.payload
    const attachedPendingIds = listenerApi.getState().timelineAssetState.addFilesStaging.attachedPendingUploadClientIds
    if (!attachedPendingIds.includes(clientTmpId)) return

    listenerApi.dispatch(TimelineAssetActions.stageUserFileIds({ fileIds: [serverFileId] }))
    listenerApi.dispatch(TimelineAssetActions.removePendingUserFileUpload(clientTmpId))
  },
})
